/* =====================================================================
 * @auw/app-shell — Workspace mode registry
 *
 * Single source of truth for per-mode presentation metadata. Tenant manifests
 * select which subset is active via `features.modes`; the shell consumes the
 * resolved list via `resolveWorkspaceModes()` (Phase 4c).
 *
 * Ordering invariant: WORKSPACE_ORDER must only contain ids that exist in
 * WORKSPACE_MODES; callers rely on this for stable sidebar rendering.
 * ===================================================================== */

import {
  Bot,
  Radio,
  FileText,
  FileSignature,
  Folder,
  GitCompareArrows,
  ClipboardList,
  Send,
  Settings2,
  ShieldCheck,
  Puzzle,
  LayoutGrid,
  Newspaper,
  Wrench,
  Wand2,
  BarChart3,
  ChartPie,
  Languages,
  Brain,
  Stamp,
  BookOpen,
  Store,
} from 'lucide-react';
import type { WorkspaceMode } from '@auw/types';
import type { WorkspaceModeInfo } from './types';

export const WORKSPACE_MODES: Record<WorkspaceMode, WorkspaceModeInfo> = {
  agent:        { icon: Bot,             labelKey: 'nav.agent',      letter: 'A', color: '#D4915A', showTopBar: true,  showInput: true,  showDragDrop: true  },
  'live-agent': { icon: Radio,           labelKey: 'nav.liveAgent',  letter: 'L', color: '#E06B50', showTopBar: true,  showInput: true,  showDragDrop: false },
  document:     { icon: FileText,        labelKey: 'nav.document',   letter: 'D', color: '#6B9EC2', showTopBar: true,  showInput: true,  showDragDrop: true  },
  'doc-agent':  { icon: FileSignature,   labelKey: 'nav.docAgent',   letter: 'D', color: '#5A8FB3', showTopBar: false, showInput: false, showDragDrop: false },
  // GTC 개정판 비교. showInput=true 는 분석 이후 후속 채팅용 (Phase 5) — 시작
  // 페이지에서는 composer 를 숨겨야 하므로 WorkspaceModeChrome 에 억제 플래그가
  // 함께 필요하다. 상세: docs/samsung/prd/agent/gtc-agent-plan-ko.md Phase 5.
  'gtc-agent':  { icon: GitCompareArrows, labelKey: 'nav.gtcAgent',  letter: 'G', color: '#7A9A8C', showTopBar: false, showInput: true,  showDragDrop: true  },
  // 구매 부서 에이전트. 선적서류 검토 작업대(전용 페이지)가 자체 헤더를
  // 그리므로 공용 TopBar 는 gtc-agent 와 동일하게 끈다 — 켜두면 빈 띠가
  // 헤더 위에 하나 더 생긴다 (translation 주석과 같은 증상).
  // 색은 기존 mode 색 + 브랜드 블루 전체와의 min ΔE00 를 최대화해 선정했다
  // (17.64, 최근접 storage #8C8C8C). 상세: docs/shi-buy-agent/design/
  //   agent-runtime-and-new-agent-strategy-ko.md §10
  'purchase-agent': { icon: ClipboardList, labelKey: 'nav.purchaseAgent', letter: 'P', color: '#918352', showTopBar: false, showInput: true,  showDragDrop: true  },
  // 공문 에이전트 — 사내 공문 업무 지원. 페이지 구현이 아직 없어 사이드바
  // 항목과 경로만 먼저 열고 준비중 안내만 띄운다. 그래서 위 두 에이전트와
  // 달리 showTopBar 를 켠다 — 자체 헤더가 없으니 공용 TopBar 없이는 사이드바를
  // 접은 뒤 다시 펼칠 버튼이 화면에서 사라진다. 전용 화면이 들어오면 자체
  // 헤더를 그리는 gtc / purchase 처럼 false 로 내린다.
  'official-doc-agent': { icon: Stamp,   labelKey: 'nav.officialDocAgent', letter: 'O', color: '#A8697A', showTopBar: true,  showInput: false, showDragDrop: false },
  storage:      { icon: Folder,          labelKey: 'nav.storage',    letter: 'R', color: '#8C8C8C', showTopBar: true,  showInput: false, showDragDrop: false },
  message:      { icon: Send,            labelKey: 'nav.message',    letter: 'M', color: '#A8889A', showTopBar: true,  showInput: false, showDragDrop: false },
  widget:       { icon: LayoutGrid,      labelKey: 'nav.widget',     letter: 'W', color: '#8F9DB5', showTopBar: true,  showInput: false, showDragDrop: false },
  feed:         { icon: Newspaper,       labelKey: 'nav.feed',       letter: 'F', color: '#7B8BA6', showTopBar: true,  showInput: false, showDragDrop: false },
  tools:        { icon: Wrench,          labelKey: 'nav.tools',      letter: 'T', color: '#7EA37A', showTopBar: true,  showInput: false, showDragDrop: false },
  // 번역 유틸리티. 자체 헤더에 모드 이름과 메인 사이드바 펼치기 버튼을
  // 직접 그리므로 (통합 에이전트의 WorkspaceListSidebar 헤더와 같은 패턴)
  // 공용 TopBar 는 끈다 — 켜두면 빈 48px 띠가 헤더 위에 하나 더 생긴다.
  translation:  { icon: Languages,       labelKey: 'nav.translation', letter: 'T', color: '#5C9E8F', showTopBar: false, showInput: false, showDragDrop: false },
  'app-manage':  { icon: Settings2,       labelKey: 'nav.appManage',  letter: 'G', color: '#8C8C8C', showTopBar: false, showInput: false, showDragDrop: false },
  'admin-page':  { icon: ShieldCheck,     labelKey: 'nav.adminPage', letter: 'A', color: '#6B7280', showTopBar: false, showInput: false, showDragDrop: false },
  plugin:        { icon: Puzzle,         labelKey: 'nav.plugin',     letter: 'P', color: '#7C6BB3', showTopBar: false, showInput: false, showDragDrop: false },
  'skill-create': { icon: Wand2,         labelKey: 'nav.skillCreate', letter: 'K', color: '#D4915A', showTopBar: false, showInput: false, showDragDrop: false },
  dashboard:     { icon: BarChart3,      labelKey: 'nav.dashboard',  letter: 'B', color: '#5B7FB3', showTopBar: false, showInput: false, showDragDrop: false },
  // 어드민 대시보드 — 사이드바 "관리" 섹션 맨 아래, 개인 대시보드 바로 다음.
  // 테넌트 전체 사용량을 본부/부서로 집계하므로 부서코드 허용목록으로 접근을
  // 제한한다 (backend `admin_dashboard_router`). dashboard 와 같은 frameless
  // 페이지라 공용 TopBar / MessageInput 은 끈다. WORKSPACE_ORDER 에는 넣지
  // 않는다 — 그 배열은 "워크스페이스" 섹션 전용이다.
  'admin-dashboard': { icon: ChartPie,   labelKey: 'nav.adminDashboard', letter: 'B', color: '#4A6E9E', showTopBar: false, showInput: false, showDragDrop: false },
  // 에이전트 메모리 — 사이드바 "관리" 섹션. 대화 이력이 없는 단발성 페이지라
  // 공용 TopBar / MessageInput 은 끄고 페이지가 자체 헤더를 그린다
  // (dashboard / plugin 과 같은 취급). WORKSPACE_ORDER 에는 넣지 않는다 —
  // 그 배열은 "워크스페이스" 섹션 전용이라 항목이 두 번 렌더된다.
  memory:        { icon: Brain,          labelKey: 'nav.memory',     letter: 'M', color: '#8A7FB3', showTopBar: false, showInput: false, showDragDrop: false },
  // 사용자 가이드 — 사이드바 어느 섹션에도 넣지 않는다. 진입점은 하단 고정
  // 배너 하나뿐이고, 가이드 목차가 사이드바 항목 목록을 그대로 비추므로
  // 섹션에 올리면 자기 자신을 가리키는 항목이 생긴다. 그래서 WORKSPACE_ORDER
  // 에서도 빠진다. frameless — 페이지가 목차 패널과 자체 헤더를 그린다.
  'user-guide':  { icon: BookOpen,       labelKey: 'nav.userGuide',  letter: 'U', color: '#5B7FB3', showTopBar: false, showInput: false, showDragDrop: false },
  // 에이전트 스킬 마켓플레이스 — 사이드바 "마켓플레이스" 섹션. 내 스킬 /
  // 레퍼런스 스킬 / 사내 공유 스킬을 한 페이지에서 다루는 단발성 페이지라
  // dashboard 와 같은 frameless 취급(공용 TopBar / MessageInput 끔).
  // WORKSPACE_ORDER 에는 넣지 않는다 — 그 배열은 "워크스페이스" 섹션 전용이다.
  'skill-marketplace': { icon: Store,    labelKey: 'nav.skillMarketplace', letter: 'S', color: '#7F8FA6', showTopBar: false, showInput: false, showDragDrop: false },
};

/**
 * Default sidebar ordering for workspace modes. Excludes management-only
 * modes (storage/app-manage/admin-page/plugin/skill-create) which render in
 * the separate "관리" section or via dedicated routes.
 *
 * Tenants can override this with `tenant.workspace.modeOrder` (Phase 4c).
 */
export const WORKSPACE_ORDER: WorkspaceMode[] = [
  'agent', 'live-agent', 'doc-agent', 'gtc-agent', 'purchase-agent',
  'official-doc-agent', 'document',
];

/**
 * Set of all known workspace-mode ids, derived from WORKSPACE_MODES at
 * module init. Kept as a Set for O(1) membership checks — `isWorkspaceMode`
 * is called once per entry in tenant-supplied `modeOrder` overrides, so
 * the allocation pays for itself after only a handful of lookups.
 */
const WORKSPACE_MODE_IDS: ReadonlySet<string> = new Set(
  Object.keys(WORKSPACE_MODES),
);

/**
 * Type guard: `true` when `value` is one of the known workspace mode ids.
 *
 * Used by tenant manifest loaders to silently drop unknown ids from
 * `modeOrder` / `defaultMode` without failing the whole manifest — this
 * matches the forgiving policy used elsewhere (e.g. `features` accepts
 * any string key).  Callers that want strict validation should layer
 * their own error reporting on top.
 */
export function isWorkspaceMode(value: unknown): value is WorkspaceMode {
  return typeof value === 'string' && WORKSPACE_MODE_IDS.has(value);
}
